import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import {
  User,
  PositionType,
  ExternalCompany,
  TtCompanyReport,
} from "@/CodegenTypes";
import type { TtFullReport } from "@/types/reportTypes";
import { first, sumBy, some } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import AccountSelectSearch from "@/components/modules/account/AccountSelectSearch.vue";
import PositionStatementsModal from "@/components/modules/report/PositionStatementsModal.vue";
import TtStrikeStatementsModal from "@/components/modules/report/TtStrikeStatementsModal.vue";
import TtOutsetStrikeStatementsModal from "@/components/modules/report/TtOutsetStrikeStatementsModal.vue";
import { negateNumber } from "@/helpers/negateNumber";
import UserBreadcrumb from "@/components/common/UserBreadcrumb.vue";
import TtTicketsModal from "@/components/modules/report/TtTicketsModal.vue";
import { canSeeTtCompanyLevel } from "@/helpers/user/canSeeTtCompanyLevel";
import { FEATURE_TOGGLES } from "@/config";
import { useRoute, useRouter } from "vue-router/composables";
import { computed, ref } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useTranslation } from "@/store/translation";


export default /*#__PURE__*/_defineComponent({
  __name: 'CollectionReport',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const selectedStartReleaseDate = computed(() => {
  const query = route.query.startReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const selectedEndReleaseDate = computed(() => {
  const query = route.query.endReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const { result: meResult, loading: meLoading } = useQuery<{ me: User }>(
  queries.me,
  {
    fetchPolicy: "cache-first",
  }
);

const { $t } = useTranslation();

const ttFullReportQuery = FEATURE_TOGGLES.useV1Legacy
  ? queries.ttFullReportLegacy
  : queries.ttFullReport;

const { result: fullReportResult, loading: fullReportLoading } = useQuery<{
  ttFullReport: TtFullReport;
}>(ttFullReportQuery, {
  releaseDateStart: selectedStartReleaseDate,
  releaseDateEnd: selectedEndReleaseDate,
  userId: computed(() => route.query.userId),
});

const ttCompanyReportQuery = FEATURE_TOGGLES.useV1Legacy
  ? queries.ttCompanyReportLegacy
  : queries.ttCompanyReport;

const { result: companyReportResult, loading: companyReportLoading } =
  useQuery<{
    ttCompanyReport: TtCompanyReport;
  }>(ttCompanyReportQuery, {
    releaseDateStart: selectedStartReleaseDate,
    releaseDateEnd: selectedEndReleaseDate,
  });

const loading = computed(
  () => meLoading.value || fullReportLoading.value || companyReportLoading.value
);

const showPositionModal = ref(false);
const showStrikeModal = ref(false);
const showOutsetStrikeModal = ref(false);
const selectedUserId = ref(null as null | User["userId"]);
const includeDownlines = ref(true as boolean);
const selectedExternalCompanyId = ref(null as null | ExternalCompany["userId"]);
const showTicketsModal = ref(false);
const showAccountSelectionModal = ref(false);

const me = computed(() => meResult.value?.me);

const ttFullReport = computed(() => fullReportResult.value?.ttFullReport);
// const ttFullReport = ref<TtUserReport>();
// watch(
//   () => fullReportResult.value?.ttFullReport,
//   (value) => {
//     if (value) {
//       ttFullReport.value = value;
//     }
//   }
// );

const ttCompanyReport = computed(
  () => companyReportResult.value?.ttCompanyReport
);

const shareClaimTable = computed(() => {
  const rows = [];

  if (downlinesWithTickets.value) {
    rows.push(
      ...downlinesWithTickets.value.map((dl) => {
        const shareClaimStatements = dl.positionStatements?.filter(
          (s) => s.positionType === PositionType.Outset
        );
        const position = 0;
        const externalShareClaim = shareClaimStatements?.length
          ? sumBy(shareClaimStatements, (s) => s.uplinePosition)
          : 0;
        return {
          userId: dl.userId.toUpperCase(),
          username: "",
          position,
          externalShareClaim,
          total: position + externalShareClaim,
        };
      })
    );
  }

  if (ttCompanyReport.value?.outsetTickets) {
    rows.push(
      ...ttCompanyReport.value.outsetTickets.rows.map((outsetTicket) => {
        const position = negateNumber(outsetTicket.position);
        const externalShareClaim = outsetTicket.externalShareClaim || 0;
        return {
          userId: outsetTicket.name,
          username: "",
          position,
          externalShareClaim: -externalShareClaim,
          total: position + -externalShareClaim,
        };
      })
    );
  }

  return {
    summary: {
      position: sumBy(rows, (row) => row.position),
      externalShareClaim: sumBy(rows, (row) => row.externalShareClaim),
      total: sumBy(rows, (row) => row.total),
    },
    rows,
  };
});

const seeCompanyLevel = computed(() => {
  return canSeeTtCompanyLevel(me.value);
});

const isNotAdminViewingAdmin = computed(() => {
  return route.query?.userId === "admin" && me.value?.userId !== "admin";
});

const selectedUserStatement = computed(() => {
  if (!ttFullReport.value) {
    return null;
  }
  return [
    ttFullReport.value.consolidated,
    ...ttFullReport.value.downlines,
  ].find((s) => s.userId === selectedUserId.value);
});

const showCompany = computed(() => {
  return route.query.userId === "admin";
});



const userTickets = computed(() => {
  if (!ttFullReport.value) {
    return null;
  }
  const user = ttFullReport.value.consolidated;
  const shareClaimPositionStatements = user.positionStatements?.filter(
    (s) => s.positionType === PositionType.Outset
  );
  if (shareClaimPositionStatements?.length) {
    const shareClaim = sumBy(
      shareClaimPositionStatements,
      (s) => s.uplinePosition
    );
    return {
      ...user,
      shareClaim,
      total: shareClaim + user.netBal,
    };
  } else {
    return {
      ...user,
      shareClaim: 0,
      total: user.netBal,
    };
  }
});

const downlinesWithTickets = computed(() => {
  const downlines = ttFullReport.value?.downlines || [];
  return downlines
    .filter((dl) =>
      some(
        Object.values(dl).filter((val) => typeof val === "number" && val !== 0)
      )
    )
    .map((dl) => {
      const shareClaimPositionStatements = dl.positionStatements?.filter(
        (s) => s.positionType === PositionType.Outset
      );
      if (shareClaimPositionStatements?.length) {
        const shareClaim = sumBy(
          shareClaimPositionStatements,
          (s) => s.uplinePosition
        );
        return {
          ...dl,
          shareClaim,
          total: shareClaim + dl.netBal,
        };
      } else {
        return {
          ...dl,
          shareClaim: 0,
          total: dl.netBal,
        };
      }
    });
});

const externalCompaniesWithTickets = computed(() => {
  return ttCompanyReport.value?.outsetTickets.rows.map((company) => {
    const shareClaim = (company.externalShareClaim || 0) * -1;
    return {
      ...company,
      userId: company.name,
      companyStake: 0,
      netBal: company.net,
      position: company.position,
      shareClaim,
      total: shareClaim + company.net,
    };
  });
});

const downlinesSummary = computed(() => {
  if (!userTickets.value) {
    return null;
  }
  if (
    userTickets.value.userId === "admin" &&
    externalCompaniesWithTickets.value
  ) {
    const combined = [
      ...downlinesWithTickets.value,
      ...externalCompaniesWithTickets.value,
    ];
    return {
      amount: sumBy(combined, (dl) => dl.amount),
      rebate: sumBy(combined, (dl) => dl.rebate),
      amountLessRebate: sumBy(combined, (dl) => dl.amountLessRebate),
      strikeAmount: sumBy(combined, (dl) => dl.strikeAmount),
      net: sumBy(combined, (dl) => dl.net),
      companyStake: sumBy(combined, (dl) => dl.companyStake),
      position: sumBy(combined, (dl) => dl.position),
      netBal: sumBy(combined, (dl) => dl.netBal),
      shareClaim: sumBy(combined, (dl) => dl.shareClaim),
      total: sumBy(combined, (dl) => dl.total),
    };
  } else {
    return {
      amount:
        userTickets.value.amount -
        sumBy(downlinesWithTickets.value, (dl) => dl.amount),
      rebate:
        userTickets.value.rebate -
        sumBy(downlinesWithTickets.value, (dl) => dl.rebate),
      amountLessRebate:
        userTickets.value.amountLessRebate -
        sumBy(downlinesWithTickets.value, (dl) => dl.amountLessRebate),
      strikeAmount:
        userTickets.value.strikeAmount -
        sumBy(downlinesWithTickets.value, (dl) => dl.strikeAmount),
      net:
        userTickets.value.net -
        sumBy(downlinesWithTickets.value, (dl) => dl.net),
      companyStake:
        userTickets.value.companyStake -
        sumBy(downlinesWithTickets.value, (dl) => dl.companyStake),
      position:
        userTickets.value.position -
        sumBy(downlinesWithTickets.value, (dl) => dl.position),
      netBal:
        userTickets.value.netBal -
        sumBy(downlinesWithTickets.value, (dl) => dl.netBal),
      shareClaim:
        userTickets.value.shareClaim -
        sumBy(downlinesWithTickets.value, (dl) => dl.shareClaim),
      total:
        userTickets.value.total -
        sumBy(downlinesWithTickets.value, (dl) => dl.total),
    };
  }
});

function showPosition(userId: User["userId"]) {
  selectedUserId.value = userId;
  showPositionModal.value = true;
}
function showStrike(userId: User["userId"]) {
  selectedUserId.value = userId;
  showStrikeModal.value = true;
}
function showTickets(userId: User["userId"], includeDownlinesInput: boolean) {
  selectedUserId.value = userId;
  includeDownlines.value = includeDownlinesInput;
  showTicketsModal.value = true;
}

function showOutsetStrike(userId: ExternalCompany["userId"]) {
  selectedExternalCompanyId.value = userId;
  showOutsetStrikeModal.value = true;
}

function seeAdmin() {
  router.push({
    name: "toto collection report",
    query: { ...route.query, userId: "admin" },
  });
}

return { __sfc: true,router, route, selectedStartReleaseDate, selectedEndReleaseDate, meResult, meLoading, $t, ttFullReportQuery, fullReportResult, fullReportLoading, ttCompanyReportQuery, companyReportResult, companyReportLoading, loading, showPositionModal, showStrikeModal, showOutsetStrikeModal, selectedUserId, includeDownlines, selectedExternalCompanyId, showTicketsModal, showAccountSelectionModal, me, ttFullReport, ttCompanyReport, shareClaimTable, seeCompanyLevel, isNotAdminViewingAdmin, selectedUserStatement, showCompany, userTickets, downlinesWithTickets, externalCompaniesWithTickets, downlinesSummary, showPosition, showStrike, showTickets, showOutsetStrike, seeAdmin, formatDollar, AccountSelectModal, AccountSelectSearch, PositionStatementsModal, TtStrikeStatementsModal, TtOutsetStrikeStatementsModal, UserBreadcrumb, TtTicketsModal, FEATURE_TOGGLES }
}

})